import { useState } from 'react';
import { observer } from 'mobx-react';
import { Tabs, Space, Typography, Button, Tag, Tooltip } from 'antd';
import {
  RocketOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  HourglassOutlined,
  ArrowRightOutlined,
  ThunderboltOutlined,
  BellOutlined,
  CompressOutlined,
  DollarCircleOutlined,
  EditOutlined,
  PushpinOutlined,
  AreaChartOutlined,
  LineChartOutlined,
  BranchesOutlined,
  UserOutlined,
  ToolOutlined,
  TeamOutlined,
  SearchOutlined,
  DashboardOutlined,
  HeartOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';

const { Text, Paragraph } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// OperationsPage — single roadmap surface for the observability investment
// ─────────────────────────────────────────────────────────────────────────────
//
// Two tabs:
//   Tab 1 — Quick wins: 8 small surface improvements. The kind operators
//           feel immediately on every page load.
//   Tab 2 — Foundational: 6 structural surfaces that take longer but
//           transform how the team works (alerts, triage, search, etc.).
//
// Each item is a status card with one of:
//   ✅ Live        — shipped; "Open" link goes to the surface
//   🚧 In progress — partially shipped; clickable to the working part
//   ⏳ Planned     — designed but not yet built; description summarizes
//                    what it'll look like when shipped
//
// Status values reflect the verified state as of 2026-05-01 from the
// observability inventory walkthrough. Update the `status` field on a
// card as it ships.
// ─────────────────────────────────────────────────────────────────────────────

type Status = 'live' | 'in-progress' | 'planned';

interface ItemCardProps {
  index: number;
  icon: React.ReactNode;
  title: string;
  description: string;
  status: Status;
  /** Path to the live surface (only used when status === 'live' or 'in-progress'). */
  link?: string;
  /** Optional short label on the action button. Defaults to "Open". */
  linkLabel?: string;
  /** Effort estimate shown as a small badge — operators want to know "is this big?" */
  effort?: string;
  isDark: boolean;
}

const STATUS_META: Record<Status, { label: string; tone: string; bg: string; border: string; icon: React.ReactNode }> = {
  live: {
    label: 'Live',
    tone: colors.green[700],
    bg: colors.green[100],
    border: colors.green[600],
    icon: <CheckCircleFilled />,
  },
  'in-progress': {
    label: 'In progress',
    tone: colors.amber[700],
    bg: colors.amber[100],
    border: colors.amber[600],
    icon: <ClockCircleOutlined />,
  },
  planned: {
    label: 'Planned',
    tone: colors.slate[700],
    bg: colors.slate[100],
    border: colors.slate[400],
    icon: <HourglassOutlined />,
  },
};

function ItemCard({ index, icon, title, description, status, link, linkLabel = 'Open', effort, isDark }: ItemCardProps) {
  const meta = STATUS_META[status];
  return (
    <div
      style={{
        background: isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderLeft: `4px solid ${meta.border}`,
        borderRadius: 10,
        padding: '16px 18px',
        display: 'flex',
        gap: 14,
        alignItems: 'flex-start',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: 999,
          background: isDark ? colors.violet[700] + '33' : colors.violet[100],
          color: isDark ? colors.violet[100] : colors.violet[700],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        {index}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
          <span style={{ fontSize: 16, color: isDark ? colors.violet[100] : colors.violet[700] }}>{icon}</span>
          <Text strong style={{ fontSize: 15, color: isDark ? colors.slate[50] : colors.slate[900] }}>
            {title}
          </Text>
          <Tag
            icon={meta.icon}
            style={{
              margin: 0,
              background: meta.bg,
              color: meta.tone,
              border: `1px solid ${meta.border}`,
              fontWeight: 600,
              fontSize: 11,
            }}
          >
            {meta.label}
          </Tag>
          {effort && (
            <Tooltip title="Engineering effort estimate.">
              <Tag style={{ margin: 0, fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>{effort}</Tag>
            </Tooltip>
          )}
        </div>
        <Paragraph type="secondary" style={{ margin: 0, fontSize: 13, lineHeight: 1.55 }}>
          {description}
        </Paragraph>
        {link && (status === 'live' || status === 'in-progress') && (
          <div style={{ marginTop: 12 }}>
            <Link to={link}>
              <Button size="small" type="primary" icon={<ArrowRightOutlined />} iconPosition="end">
                {linkLabel}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tier 1 — Quick wins
// ─────────────────────────────────────────────────────────────────────────────

const QUICK_WINS: Omit<ItemCardProps, 'isDark'>[] = [
  {
    index: 1,
    icon: <HeartOutlined />,
    title: 'Header health beacon',
    description:
      "Tiny status pill in the global top bar (green / amber / red) that polls every 60s; click → tray with active alerts. Operators glance once on every page load and know whether to escalate. Today: not built — operators have to remember to check the Insights page.",
    status: 'planned',
    effort: '½ day',
  },
  {
    index: 2,
    icon: <ThunderboltOutlined />,
    title: 'Recent runs strip on the Dashboard',
    description:
      "Replace the dashboard's empty-state area with the last 20 AI processes color-coded by status; click → Investigation Drawer. Today: dashboard surfaces Top pressures but not the kinetic feed of recent activity.",
    status: 'planned',
    effort: '½ day',
  },
  {
    index: 3,
    icon: <BranchesOutlined />,
    title: 'Inline link cascade everywhere',
    description:
      "Every breadcrumb is clickable: Activity row → agent detail → skill body → KB chunk that fired → tool → reviewer notes. Currently ~60% wired (Activity → agent works; agent → skill → KB body chain is partial).",
    status: 'in-progress',
    effort: '½ day',
    link: '/activity',
    linkLabel: 'See the live chain on /activity',
  },
  {
    index: 4,
    icon: <DollarCircleOutlined />,
    title: 'Cost + latency chips on Activity rows',
    description:
      "Every Activity row carries inline `$0.0400` and `5.8s` badges so operators can scan-spot outliers at the index page without opening the drawer. Already shipped — visible on /activity rows.",
    status: 'live',
    link: '/activity',
    linkLabel: 'Open Activity',
  },
  {
    index: 5,
    icon: <EditOutlined />,
    title: 'Recent edits feed (last 24h)',
    description:
      "Single timeline of every editorial change — skill bodies, KB reloads, agent instructions, system prompts, scenario edits. Sourced from the audit/history tables we already write to (ai.PrebuiltPromptHistory, ai.AgentInstructionHistory). Closes the loop on 'did my change cause this?'",
    status: 'in-progress',
    effort: '½ day',
    link: '/prompts',
    linkLabel: 'See per-prompt history on /prompts',
  },
  {
    index: 6,
    icon: <PushpinOutlined />,
    title: 'Pinned watchlist',
    description:
      "Operators pin the agents they're babysitting; pinned strip on the dashboard with last-touched, error rate, and current alert chips per pinned agent. Personalization makes the dashboard feel like theirs. Today: pinning exists for individual agents but no consolidated dashboard strip.",
    status: 'planned',
    effort: '½ day',
  },
  {
    index: 7,
    icon: <LineChartOutlined />,
    title: 'Deploy markers on Insights charts',
    description:
      "Vertical line + tooltip ('deployed v1.42 May 1 14:30; agent X edit 16:02') on every time-series in /insights. Half a day of work; massive A/B + causality clarity for regression hunts. Today: charts have no annotation layer — and we don't yet log deploys to a queryable table.",
    status: 'planned',
    effort: '½ day',
  },
  {
    index: 8,
    icon: <AreaChartOutlined />,
    title: 'Per-agent sparklines on the Agents roster',
    description:
      "Three thumb-sized 24h sparklines per row on the /agents page: call volume, error rate, P95 latency. Converts the agent table from a list into a comparator. Today: roster shows static fields only.",
    status: 'planned',
    effort: '½ day',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Tier 2 — Foundational
// ─────────────────────────────────────────────────────────────────────────────

const FOUNDATIONAL: Omit<ItemCardProps, 'isDark'>[] = [
  {
    index: 9,
    icon: <BellOutlined />,
    title: 'Alerts inbox + Slack/email/push delivery',
    description:
      "Operator-facing rules engine: 'error rate > 3% over 15m', 'P95 > 5s', 'safety flag fires', 'cost spike > 2× baseline'. Acknowledge / snooze / assign workflow. Operators don't sit in dashboards — they need to be paged. Requires new alert-rule schema + delivery integration.",
    status: 'planned',
    effort: '3-5 days',
  },
  {
    index: 10,
    icon: <CompressOutlined />,
    title: 'Conversation triage',
    description:
      "Ranked list of conversations (not events) by bad-signal heuristics: long, looping, near-context-cliff, high-cost, abandoned. The currently-shipped Activity is event-level; operators think and triage in conversations. Now partially live via the Conversation Cost (#12) and Context Cliff (#11) tiles on /insights.",
    status: 'in-progress',
    effort: '3 days for dedicated page',
    link: '/insights',
    linkLabel: 'See live tiles on /insights',
  },
  {
    index: 11,
    icon: <UserOutlined />,
    title: 'Per-patient timeline',
    description:
      "Given a member id, show every agent interaction across the day/week with sentiment + outcome. Bridges the agent perspective with the patient one — clinical leads need this for case reviews. Requires new aggregation queries.",
    status: 'planned',
    effort: '2-3 days',
  },
  {
    index: 12,
    icon: <ToolOutlined />,
    title: 'Skill + KB utilization heatmap',
    description:
      "For each agent: which skills fired vs. were merely loaded, which KB chunks were retrieved vs. dormant. Surfaces dead weight (skills nobody hits, KB sections never queried). Dramatically cuts prompt budget waste. Requires new per-skill / per-chunk hit-count rollup query.",
    status: 'planned',
    effort: '2-3 days',
  },
  {
    index: 13,
    icon: <TeamOutlined />,
    title: 'Reviewer queue health',
    description:
      "Pending approvals by reviewer, oldest pending age, throughput last 7d, average review latency. Today's Reviewers page lists the people but not the load. Lets operations re-balance assignments before backlogs build up.",
    status: 'in-progress',
    effort: '1-2 days',
    link: '/reviewers',
    linkLabel: 'Open /reviewers (basic list)',
  },
  {
    index: 14,
    icon: <SearchOutlined />,
    title: 'Global search bar — thread / member / agent / free text',
    description:
      "Top-bar Cmd+K palette that searches thread ids, correlation ids, member ids, agent names, and free-text inside conversation messages. Operators paste a thread id from Slack and want to land on the trace in 1 keystroke. Today: header has agent/run search but not free-text content search.",
    status: 'in-progress',
    effort: '2 days',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

export const OperationsPage = observer(function OperationsPage() {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [activeTab, setActiveTab] = useState<string>('quickWins');

  // Counts for the tab labels — operators see at a glance how much of each
  // tier is shipped. Updates automatically as you flip status from
  // 'planned' / 'in-progress' to 'live' on a card.
  const liveQuickWins = QUICK_WINS.filter((q) => q.status === 'live').length;
  const liveFoundational = FOUNDATIONAL.filter((f) => f.status === 'live').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingBottom: 32 }}>
      <PageHeader
        icon={<RocketOutlined />}
        accent="violet"
        title="Operations"
        subtitle="Status of the operator-grade observability investment. Two tiers — Quick wins (½-day each) and Foundational (multi-day, structural). Live items deep-link to the working surface."
      />

      <SectionCard noPadding>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          tabBarStyle={{ padding: '8px 18px 0 18px', marginBottom: 0 }}
          items={[
            {
              key: 'quickWins',
              label: (
                <span>
                  <DashboardOutlined /> Quick wins{' '}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {liveQuickWins} / {QUICK_WINS.length} live
                  </Text>
                </span>
              ),
              children: (
                <div style={{ padding: '6px 18px 18px 18px' }}>
                  <Paragraph type="secondary" style={{ marginBottom: 14, maxWidth: 760, fontSize: 13 }}>
                    Small surfaces with outsized operator value. Each ships in about half a day, runs on data we already
                    have, and changes how the app feels rather than how it's structured.
                  </Paragraph>
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {QUICK_WINS.map((item) => (
                      <ItemCard key={item.index} {...item} isDark={isDark} />
                    ))}
                  </Space>
                </div>
              ),
            },
            {
              key: 'foundational',
              label: (
                <span>
                  <ApiOutlined /> Foundational{' '}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {liveFoundational} / {FOUNDATIONAL.length} live
                  </Text>
                </span>
              ),
              children: (
                <div style={{ padding: '6px 18px 18px 18px' }}>
                  <Paragraph type="secondary" style={{ marginBottom: 14, maxWidth: 760, fontSize: 13 }}>
                    Structural investments — alerts, conversation-grain triage, search, reviewer queue health,
                    cross-agent timelines. 2–5 days each. Ship one at a time so each lands as a coherent surface, not
                    a half-built tile.
                  </Paragraph>
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {FOUNDATIONAL.map((item) => (
                      <ItemCard key={item.index} {...item} isDark={isDark} />
                    ))}
                  </Space>
                </div>
              ),
            },
          ]}
        />
      </SectionCard>
    </div>
  );
});

